import React, {
  useCallback, useEffect,
} from 'react';
import {
  SUBSCRIPTIONS_CATEGORY,
} from '@/enums/billing/billing.enum';
import {
  Alert,
  Button,
  IconCourses,
  IconDomain,
  IconEmail as IconEmailV2,
  IconFile,
  Stepper,
  Tag,
  IconSecurity,
} from 'gatorcomponents';

import * as Styles from './SubscriptionItem.styles';
import { isBRServer } from '@/utils/Validators/validation';
import { useHistory } from 'react-router';

const SubscriptionItem = ({
  index,
  activeStatusLabel = 'Active',
  buttonManageSubscriptionsLabel = 'Manage',
  addonListLabel = 'Addons',
  cancelledStatusLabel = 'Cancelled',
  domainLabel = 'Domain',
  expiredStatusLabel = 'Expired',
  onSubscriptionManageClick,
  overdueStatusLabel = 'Overdue',
  pendingAnalysisStatusLabel = 'Pending Analysis',
  pendingPaymentStatusLabel = 'Pending Payment',
  pendingStatusLabel = 'Pending',
  registeredStatusLabel = 'Registred',
  subscription,
  suspendedStatusLabel = 'Suspended',
  testId = 'subscription-item',
  toDueStatusLabel = 'To due',
  transferredAwayStatusLabel = 'Transferred',
  orderStatusLocale,
  routesLocale,
  readMoreStepper,
  paymendMethodType,
  isCreditCard,
  isChildrenSubscriptionList = false,
}) => {
  const history = useHistory();

  const {
    domain,
    id,
    productName,
    type,
    substatus,
    category,
  } = subscription;

  const getIcon = (category) => {
    const iconsLayout = {
      [SUBSCRIPTIONS_CATEGORY.DOMAIN]: () => <IconDomain />,
      [SUBSCRIPTIONS_CATEGORY.COURSE]: () => <IconCourses />,
      [SUBSCRIPTIONS_CATEGORY.OTHERS]: () => <IconSecurity />,
      [SUBSCRIPTIONS_CATEGORY.SECURITY]: () => <IconSecurity />,
      [SUBSCRIPTIONS_CATEGORY.EMAIL]: () => <IconEmailV2 />,
      [SUBSCRIPTIONS_CATEGORY.HOSTING]: () => <IconFile />,
      default: () => <IconFile />,
    };

    const icons = iconsLayout;

    return (icons[category] || icons.default)();
  };

  const clearSubscriptionInfoBySupport = useCallback(() => {
    localStorage.setItem('subscriptionInfoBySupport', JSON.stringify({}));
  }, []);

  useEffect(() => {
    clearSubscriptionInfoBySupport();
  }, [clearSubscriptionInfoBySupport]);

  const handleOnClick = useCallback(() => {
    localStorage.setItem('subscriptionInfoBySupport', JSON.stringify({ product_id: id, product_name: productName }));
    onSubscriptionManageClick && onSubscriptionManageClick({ id, type });
  }, [onSubscriptionManageClick, id, type, productName]);

  const getStatus = () => substatus;

  const getStatusLabel = () => {
    const labelsByStatus = {
      active: activeStatusLabel,
      cancelled: cancelledStatusLabel,
      expired: expiredStatusLabel,
      overdue: overdueStatusLabel,
      pending: pendingStatusLabel,
      pendingAnalysis: pendingAnalysisStatusLabel,
      pendingPayment: pendingPaymentStatusLabel,
      registered: registeredStatusLabel,
      suspended: suspendedStatusLabel,
      toDue: toDueStatusLabel,
      transferredAway: transferredAwayStatusLabel,
    };
    const labelSelected = labelsByStatus[getStatus()];
    return labelSelected ? labelSelected.toUpperCase() : '';
  };

  const statusTagVariants = {
    active: 'active',
    cancelled: 'cancel',
    error: 'problem',
    expired: 'problem',
    overdue: 'problem',
    pendingAnalysis: 'pending',
    pendingPayment: 'pending',
    pending: 'pending',
    registered: 'active',
    suspended: 'problem',
    toDue: 'attention',
    transferredAway: 'pending',
  };

  const statusAlertVariants = {
    active: 'error',
    cancelled: '',
    error: 'error',
    expired: 'error',
    overdue: 'error',
    pendingAnalysis: 'warning',
    pendingPayment: 'warning',
    pending: 'warning',
    registered: 'error',
    suspended: 'error',
    toDue: 'warning',
    transferredAway: 'warning',
  };

  const title = type === 'domain' ? `${domainLabel} - ${domain}` : productName;

  const { orderUnderReview } = subscription;

  const shouldRenderStepper = (substatus === 'pending' || substatus === 'cancelled') && isBRServer;

  const hasDate = date => date && date !== '0000-00-00 00:00:00';

  const getAnalysisStatus = () => {
    if (isCreditCard && hasDate(orderUnderReview.date_order) && orderUnderReview.status === 'pending_analysis') {
      return 'ongoing';
    }

    if (!isCreditCard && (!hasDate(orderUnderReview.date_paid) || !hasDate(orderUnderReview.date_reg_analysis))) {
      return 'pending';
    }

    if (orderUnderReview.status === 'cancelled') {
      return 'finished';
    }

    return 'ongoing';
  };

  const getAnalysisTooltip = () => {
    if (isCreditCard && hasDate(orderUnderReview.date_order) && orderUnderReview.status === 'pending_analysis') {
      return { content: orderStatusLocale.analysing.tooltip };
    }

    if (!isCreditCard && (!hasDate(orderUnderReview.date_paid) || !hasDate(orderUnderReview.date_reg_analysis))) {
      return null;
    }

    if (orderUnderReview.status === 'cancelled') {
      return null;
    }

    return { content: orderStatusLocale.analysing.tooltip };
  };

  const getPaidTooltip = () => {
    if (isCreditCard) {
      return { content: orderStatusLocale.paid.finished.tooltip.preAuth };
    }

    if (!isCreditCard && !hasDate(orderUnderReview.date_paid)) {
      return { content: orderStatusLocale.paid.ongoing.tooltip };
    }

    return undefined;
  };

  const getStepperProps = () => {
    const defaultProps = {
      direction: 'row',
      statusColor: substatus === 'pending' ? 'pendingMedium' : 'lowLight',
    };

    const itemsWithOrder = orderUnderReview && [
      {
        id: 0,
        status: 'finished',
        text: {
          title: orderStatusLocale.buy.title,
        },
      },
      {
        id: 1,
        status: (hasDate(orderUnderReview.date_paid) || isCreditCard) ? 'finished' : 'ongoing',
        text: {
          title: (hasDate(orderUnderReview.date_paid) || isCreditCard)
            ? orderStatusLocale.paid.finished.title[paymendMethodType]
            : orderStatusLocale.paid.ongoing.title,
        },
        tooltip: getPaidTooltip(),
      },
      {
        id: 2,
        status: getAnalysisStatus(),
        text: {
          title: orderStatusLocale.analysing[hasDate(orderUnderReview.date_update_analysis) ? 'cancelled' : 'pending'].title,
          lightTitle: orderStatusLocale.analysing[hasDate(orderUnderReview.date_update_analysis) ? 'cancelled' : 'pending'].lightTitle,
        },
        tooltip: getAnalysisTooltip(),
      },
      {
        id: 3,
        status: orderUnderReview.status === 'cancelled' ? 'error' : 'pending',
        text: {
          title: orderStatusLocale.finalStatus[hasDate(orderUnderReview.date_update_analysis) ? substatus : 'pending'].title,
          lightTitle: orderStatusLocale.finalStatus[hasDate(orderUnderReview.date_update_analysis) ? substatus : 'pending'].lightTitle,
        },
        link: substatus === 'cancelled' && {
          label: readMoreStepper,
          href: `${routesLocale.subscriptions}${routesLocale.manageSubscription}/${id}/${type}`,
          history,
          target: '_self',
        },
      },
    ];

    const itemsWithoutOrder = [
      {
        id: 0,
        status: 'finished',
        text: {
          title: orderStatusLocale.buy.title,
        },
      },
      {
        id: 1,
        status: 'ongoing',
        text: {
          title: orderStatusLocale.paid.ongoing.title,
        },
        tooltip: { content: orderStatusLocale.paid.ongoing.tooltip },
      },
      {
        id: 2,
        status: 'pending',
        text: {
          title: orderStatusLocale.analysing.pending.title,
          lightTitle: orderStatusLocale.analysing.pending.lightTitle,
        },
      },
      {
        id: 3,
        status: 'pending',
        text: {
          title: orderStatusLocale.finalStatus.pending.title,
          lightTitle: orderStatusLocale.finalStatus.pending.lightTitle,
        },
      },
    ];

    const items = orderUnderReview ? itemsWithOrder : itemsWithoutOrder;

    return {
      ...defaultProps,
      items,
    };
  };

  return (
    <Styles.Wrapper
      data-testid={testId}
      isChildrenSubscriptionList={isChildrenSubscriptionList}
    >
      {(isChildrenSubscriptionList && index === 0) && (
        <Styles.WrapperAddonListLabel data-testid={`${testId}-addon-list-label`}>{addonListLabel}</Styles.WrapperAddonListLabel>
      )}

      <Styles.WrapperContent>
        <Styles.SubscriptionInfo>
          <Styles.Icon>
            {getIcon(category)}
          </Styles.Icon>
          <Styles.SubscriptionInfoDetails>
            <Styles.ProductName title={title} data-testid={`${testId}-title`}>
              {title}
            </Styles.ProductName>
            {(type !== 'domain' && !isChildrenSubscriptionList) && (
              <Styles.Domain title={domain} data-testid={`${testId}-domain`}>
                {domain}
              </Styles.Domain>
            )}
          </Styles.SubscriptionInfoDetails>
        </Styles.SubscriptionInfo>
        <Styles.Status>
          <Tag
            variant={statusTagVariants[getStatus()]}
            label={getStatusLabel()}
            testId="tag-subscription-status"
          />
        </Styles.Status>
        <Styles.MessageWrapper>
          {(shouldRenderStepper && false) && (
            <Styles.StepperWrapper data-testid="StepperWrapper" id="StepperWrapper" cancelled={substatus === 'cancelled'}>
              <Stepper {...getStepperProps()} />
            </Styles.StepperWrapper>
          )}
          {(subscription.message && subscription.message.text) && (
            <Alert
              description={subscription.message.text}
              variant={statusAlertVariants[getStatus()]}
              centerIcon="true"
              withoutBackground="true"
              customIcon="IconDanger"
            />
          )}
        </Styles.MessageWrapper>
        <Styles.SubscriptionManage>
          <Button
            testId={`${testId}-button`}
            variant={isChildrenSubscriptionList ? 'tertiary' : 'secondary'}
            label={buttonManageSubscriptionsLabel}
            onClick={handleOnClick}
            size="medium2"
          />
        </Styles.SubscriptionManage>
      </Styles.WrapperContent>
    </Styles.Wrapper>
  );
};

export default SubscriptionItem;
